import { SUBMIT_CART } from "@/actions/SUBMIT_CART.ts";
import { UserInfoFI } from "@/components/HolderForm/UserInfoFI.ts";
import { isMandarinoCamp } from "@/helpers/customFeatures.ts";
import { useGTMPurchase } from "@/helpers/gtm/useGTMPurchase.ts";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import {
  ParcelObjectType,
  PaymentProviderEnum,
  PaymentStatusEnum,
  PaymentType,
  SubmitCartInput,
} from "@/helpers/types/types.ts";
import { useCalculateCart } from "@/helpers/useCalculateCart.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { cleanupScrollLocks } from "@/pages/BookingEngine/hooks/cleanupScrollLocks.ts";
import { getCarrierInfoInput } from "@/pages/ReservationOverview/helpers/getCarrierInfoInput.ts";
import { getCartAccommodationInput } from "@/pages/ReservationOverview/helpers/getCartAccommodationInput.ts";
import { showPaymentRedirectOverlay } from "@/pages/ReservationOverview/helpers/showPaymentRedirectOverlay.tsx";
import { SCInput, SCPayload } from "@/pages/ReservationOverview/interfaces/submitCartInterfaces.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { ApolloError, useMutation } from "@apollo/client";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const useReservationSubmit = (onSubmitComplete: () => void) => {
  const { t } = useTranslation();
  const { paymentEnabled, paymentType } = useGetOrganization();
  const handleGTMPurchase = useGTMPurchase();

  const {
    cartAccommodationItems,
    calculatedCartData,
    reservationHolderData,
    paymentData,
    isReserved,
    setIsReserved,
    setPaymentData,
  } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      calculatedCartData: state.calculatedCartData,
      reservationHolderData: state.reservationHolderData,
      paymentData: state.paymentData,
      isReserved: state.isReserved,
      setIsReserved: state.setIsReserved,
      setPaymentData: state.setPaymentData,
    })),
  );

  const note = reservationHolderData?.[UserInfoFI.Note];
  const { handleCalculateCart, calculateCartLoading } = useCalculateCart();
  const isPaymentRedirect = useRef(false);

  const handleOnError = (error: ApolloError) => {
    const isPaymentError = error?.graphQLErrors?.some((e) => e.extensions?.code === "PAYMENT_ERROR");
    if (isPaymentError) {
      alert(t("form.paymentErr") as string);
      return;
    } else {
      alert(`${error?.message}. ${t("form.submitErr") as string}`);
    }

    // If the request fails, use calculateCartMutation
    handleCalculateCart()
      ?.then(() => {
        // TODO: Do something with the calculated data if needed
      })
      .catch((calculationError) => {
        console.error("Error while recalculating cart:", calculationError);
      });
  };

  const handleOnCompleted = (data: SCPayload) => {
    const reservationData = data?.submitCart?.reservation;
    const paymentStatusData = data?.submitCart?.paymentStatus;

    if (reservationData?.id) {
      handleGTMPurchase(reservationData?.id);
      setPaymentData(undefined);

      onSubmitComplete();

      // redirects to payment provider if proper status
      const isStatusRedirect = paymentStatusData?.paymentStatus === PaymentStatusEnum.PaymentRedirect;
      setIsReserved(!isStatusRedirect);

      if (isStatusRedirect) {
        isPaymentRedirect.current = true;
        void showPaymentRedirectOverlay();

        // Timeout added to ensure UI updates
        setTimeout(() => {
          const redirectUrl = paymentStatusData?.redirectUrl;
          if (redirectUrl) {
            cleanupScrollLocks();
            window.location.href = redirectUrl;
          }
        }, 200);
      }
    }
  };

  const [submitCart, { loading: submitCartLoading }] = useMutation<SCPayload, SCInput>(SUBMIT_CART, {
    errorPolicy: "all",
    onCompleted: handleOnCompleted,
    onError: handleOnError,
  });

  const handleSubmitCart = () => {
    if (!cartAccommodationItems?.length || !reservationHolderData || !calculatedCartData) return;

    // Must be here for correct state
    const inquiryOverride = useEngineStore.getState().inquiryOverride;

    // Prepend caravan mover info to note for Mandarino camp
    let finalNote = note;
    if (isMandarinoCamp()) {
      const caravanItem = cartAccommodationItems.find(
        (item) =>
          item.parcelObjectType === ParcelObjectType.Caravan && item.parcelObjectHasMover !== undefined,
      );
      if (caravanItem) {
        const moverText = caravanItem.parcelObjectHasMover
          ? t("campSpecific.caravanHasMoverNote")
          : t("campSpecific.caravanNoMoverNote");
        finalNote = note ? `${moverText}\n${note}` : moverText;
      }
    }

    const data: SubmitCartInput = {
      accommodations: getCartAccommodationInput(cartAccommodationItems),
      totalAmount: calculatedCartData.priceBreakdown?.withDiscount,
      carrier: getCarrierInfoInput(reservationHolderData),
      note: finalNote,
      isInquiry: inquiryOverride ? true : isInquiryPaymentType(calculatedCartData),
    };

    // ___ Card guarantee: always send card data (even for inquiry, card is just guaranteed not charged) ___
    const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;
    if (paymentType === PaymentProviderEnum.WsPayPer && isCardGuarantee) {
      data.payment = paymentData;
    }

    // ___ With payment final submit ___
    const shouldSendPaymentData =
      !isInquiryPaymentType(calculatedCartData) && paymentEnabled && !inquiryOverride;

    if (shouldSendPaymentData) {
      return submitCart({ variables: { input: data } });
    }

    // ___ No payment (inquiry) final submit ___
    return submitCart({ variables: { input: data } });
  };

  return {
    handleSubmitCart,
    calculateCartLoading,
    submitCartLoading,
    isReserved,
    isPaymentRedirect,
  };
};
